import authService from "@/services/auth.service";
import { can } from "@/hooks/usePermissions";
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";

type PortalMode = "admin" | "member";

interface AuthContextValue {
  user: any | null;
  setCurrentUser: (userData: any) => void;
  logout: () => void;
  loaderUser: () => void;
  loading: boolean;
  portalMode: PortalMode;
  switchPortal: (mode: PortalMode) => void;
  canSwitchPortal: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setloading] = useState(true);
  const [portalMode, setPortalMode] = useState<PortalMode>(() => {
    const stored = localStorage.getItem("portalMode");
    return stored === "member" || stored === "admin" ? stored : "admin";
  });

  const canSwitchPortal = (() => {
    if (!user) return false;
    if (!user.memberId && !user.teacherId) return false;
    return can(user, "portal.view");
  })();

  const switchPortal = (mode: PortalMode) => {
    setPortalMode(mode);
    localStorage.setItem("portalMode", mode);
  };

  const setCurrentUser = (user:any) => {
    console.log("AuthContext - setCurrentUser called with:", user);
    console.log("AuthContext - leadershipRole:", user?.leadershipRole || user?.role?.name);
    console.log("AuthContext - isAdmin:", user?.isAdmin);
    setloading(false);
    if (user) {
      // Ensure leadershipRole is preserved when setting user
      const userWithLeadershipRole = {
        ...user,
        leadershipRole: user.leadershipRole || user.role?.name || (typeof user.role === 'string' && ["President", "Secretary", "Accountant"].includes(user.role) ? user.role : null)
      };
      console.log("AuthContext - User with leadershipRole:", userWithLeadershipRole.leadershipRole);
      setUser(userWithLeadershipRole);
      const isSystemAdminOnly = user.isSystemUser && user.isAdmin && !user.memberId && !user.teacherId;
      const isCoopLeader = ["President", "Secretary", "Accountant", "Admin", "Technical Support"].includes(
        user.leadershipRole || user.role?.name || (typeof user.role === "string" ? user.role : null)
      );
      if (!isSystemAdminOnly && !isCoopLeader && !user.isAdmin) {
        setPortalMode("member");
        localStorage.setItem("portalMode", "member");
      } else {
        // Admins / coop leaders always get admin portal mode
        setPortalMode("admin");
        localStorage.setItem("portalMode", "admin");
      }
    } else {
      setUser(undefined);
    }
  };

  const logout = () => {
    setUser(null);
  };

  const loaderUser = async () => {
    try {
      // First, try to load from localStorage to show something immediately
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          console.log("AuthContext - Loaded user from localStorage:", parsedUser);
          console.log("AuthContext - Leadership role from localStorage:", parsedUser?.leadershipRole || parsedUser?.role?.name);
          // Set it temporarily while we fetch fresh data
          setUser(parsedUser);
        } catch (e) {
          console.error("AuthContext - Error parsing stored user:", e);
        }
      }
      
      // Then fetch fresh data from server
      const authData: any = await authService.getCurrentUser();
      console.log("AuthContext - Fresh user data from server:", authData);
      console.log("AuthContext - Leadership role from server:", authData?.leadershipRole || authData?.role?.name);
      if (authData) {
        setCurrentUser(authData);
      } else {
        // API returned null but we may have a stored user — keep it and stop loading
        console.warn("AuthContext - getCurrentUser returned null, keeping stored user");
        setloading(false);
      }
    } catch (error: any) {
      console.error("AuthContext - Error loading user:", error);
      // If token expired, clear user and redirect to login
      if (error?.code === "TOKEN_EXPIRED") {
        setUser(null);
        setloading(false);
        window.location.href = "/login";
        return;
      }
      // For other errors, keep stored user if available
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        console.warn("AuthContext - API error but stored user exists, keeping stored user");
        setloading(false);
      } else {
        setCurrentUser(undefined);
      }
    }
  };

  const contextValue = useMemo(
    () => ({
      user,
      setCurrentUser,
      logout,
      loading,
      loaderUser,
      setloading,
      portalMode,
      switchPortal,
      canSwitchPortal,
    }),
    [user, loading, portalMode, canSwitchPortal]
  );

  useEffect(() => {
    loaderUser();
  }, []);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    // Provide a default context if not found (shouldn't happen if App.tsx has AuthProvider)
    console.error("⚠️ useAuth called outside of AuthProvider. Check that your component is wrapped with AuthProvider in App.tsx");
    return {
      user: null,
      setCurrentUser: () => {},
      logout: () => {},
      loading: true,
      loaderUser: async () => {},
      portalMode: "admin" as PortalMode,
      switchPortal: () => {},
      canSwitchPortal: false,
    } as AuthContextValue;
  }
  return context;
}
